import React, { useContext, useEffect, useState } from "react";
import { appTitle, snakeToCamel } from "../common/Helper";
import { useGetServerProductsQuery } from "../services/ServerProductService";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Header from "../components/Header/Header";
import Footer from "../components/shared/Footer";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@mui/icons-material";
import { AppColors } from "../common/AppColors";
import { useSelector } from "react-redux";
import { getProfile } from "../reduxenv/Slices/ProfileSlice";
import { ServerProductModel } from "../models/ServerProductModel";
import ServerProductsSlider from "../components/shared/ServerProductsSlider";
import { StyleContext } from "../context/StyleContextProvider";
import { Wallpaper } from "../components/shared/Wallpaper";
import { ServerProductSearchBar } from "../components/shared/ServerProductsSearch";
import { useParams } from "react-router-dom";

export const ServerProductsPage: React.FC = () => {
  const styleContext = useContext(StyleContext);
  const { keyword } = useParams();
  const [products, setProducts] = useState<ServerProductModel[]>([]);
  const [page, setPage] = useState<number>(1);
  const profile = useSelector(getProfile);
  const [styles, setStyles] = useState(
    styleContext.getComponentStyle("homePage"),
  );
  const isMobile = useMediaQuery("(max-width: 786px)");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const resultLimit = 24;

  const {
    data: response,
    isFetching,
    error,
  } = useGetServerProductsQuery(
    {
      countryId: profile.country.id,
      limit: resultLimit,
      page: page,
      keyword: searchQuery ?? "",
    },
    { refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    setStyles(styleContext.getComponentStyle("homePage"));
  }, [isMobile]);

  useEffect(() => {
    setSearchQuery(keyword!);
  }, [keyword]);
  useEffect(() => {
    if (response?.status === "success") {
      const results = snakeToCamel(response.response) as ServerProductModel[];
      setProducts(results);
    }
  }, [response]);

  const handleChangePage = (goNext = false) => {
    if (goNext) {
      setPage((prev) => prev + 1);
    } else {
      setPage((prev) => (prev > 1 ? prev - 1 : 1));
    }
  };

  return (
    <>
      <Helmet>
        <title>{appTitle + " - Server Products"}</title>
      </Helmet>

      <Header hideSearchBar={true} />
      <Wallpaper content={<ServerProductSearchBar styles={styles.search} />} />

      <Container maxWidth="lg">
        <Grid container sx={{ mt: 5, mb: 4 }}>
          <Grid item lg={12} xs={12}>
            <ServerProductsSlider
              rows={2}
              slidesPerRow={4}
              title={
                searchQuery
                  ? `Search Results for '${searchQuery}'`
                  : "All Server Products"
              }
              productList={products}
              isFetching={isFetching}
              error={error}
              enableNoProductsMessage
            />
          </Grid>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              marginLeft: "auto",
              mt: 2,
              width: "fit-content",
            }}
          >
            <IconButton
              sx={{
                bgcolor: AppColors.LightGray,
                border: `1px solid ${AppColors.LightGray}`,
              }}
              onClick={() => handleChangePage(false)}
              disabled={page === 1}
            >
              <ArrowLeftOutlined />
            </IconButton>

            <Typography variant="button" fontWeight="bold">
              Page {page}
            </Typography>

            <IconButton
              sx={{
                bgcolor: AppColors.LightGray,
                border: `1px solid ${AppColors.LightGray}`,
              }}
              onClick={() => handleChangePage(true)}
              disabled={products.length < resultLimit}
            >
              <ArrowRightOutlined />
            </IconButton>
          </Stack>
        </Grid>
      </Container>

      <Footer />
    </>
  );
};
